import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';

// Tooltip box geometry — keep in sync with the className below (w-60 = 240px).
const TIP_W = 240;
const MARGIN = 8;

// Small info icon with a tooltip that appears INSTANTLY on hover (no native
// `title` delay). Rendered through a portal to document.body so the metrics
// card's overflow-hidden can never clip it. The position is clamped to the
// window so the tooltip never spills outside the app frame (matters in the
// narrow Pocket desktop window), and flips below the icon when there's no room
// above. Tap toggles it on touch devices.
const InfoHint = ({ text, className = '', size = 12 }) => {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, below: false });

  const place = useCallback(() => {
    const r = ref.current && ref.current.getBoundingClientRect();
    if (!r) return;
    const vw = typeof window !== 'undefined' ? window.innerWidth : 360;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 640;
    const half = Math.min(TIP_W, vw * 0.78) / 2;
    // Center over the icon, but keep the whole tooltip inside [MARGIN, vw-MARGIN].
    let left = r.left + r.width / 2;
    left = Math.max(half + MARGIN, Math.min(left, vw - half - MARGIN));
    // Prefer above; flip below only if the top would be clipped and there's room.
    const below = r.top < 96 && vh - r.bottom > 96;
    setPos({ top: below ? r.bottom : r.top, left, below });
  }, []);

  const show = () => { place(); setOpen(true); };
  const hide = () => setOpen(false);

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  if (!text) return null;

  return (
    <>
      <span
        ref={ref}
        onMouseEnter={show}
        onMouseLeave={hide}
        onClick={(e) => { e.stopPropagation(); open ? hide() : show(); }}
        className={`inline-flex items-center align-middle cursor-pointer transition-colors ${className || 'text-slate-400 hover:text-slate-200'}`}
      >
        <Info size={size} />
      </span>
      {open && typeof document !== 'undefined' && createPortal(
        <div
          style={{
            position: 'fixed',
            top: pos.below ? pos.top + 8 : pos.top - 8,
            left: pos.left,
            transform: pos.below ? 'translate(-50%, 0)' : 'translate(-50%, -100%)',
            zIndex: 9999,
            pointerEvents: 'none',
          }}
          className="w-60 max-w-[78vw] rounded-lg bg-slate-800 border border-white/10 px-3 py-2 text-xs leading-relaxed text-slate-200 shadow-xl"
        >
          {text}
        </div>,
        document.body
      )}
    </>
  );
};

export default InfoHint;
